import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import {
  Terminal,
  Check,
  X,
  Lightbulb,
  Loader2,
  Send,
  Zap,
} from 'lucide-react';

// Miami Vice themed retro computer terminal cipher interface
// Evokes 1980s hacker movies with CRT effects and neon aesthetics
export const TerminalUI = ({
  ciphertext,
  hints,
  onSubmit,
  loading,
  result,
  attempts,
  solved,
  cipherType,
}) => {
  const [solution, setSolution] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [commandHistory, setCommandHistory] = useState([]);
  const inputRef = useRef(null);
  const terminalRef = useRef(null);

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCursorVisible(v => !v);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  // Typewriter effect for initial display
  useEffect(() => {
    const fullText = `> ENCRYPTED TRANSMISSION INTERCEPTED\n> DECRYPTION REQUIRED\n> CIPHER TYPE: ${cipherType?.toUpperCase() || 'UNKNOWN'}\n> AWAITING INPUT...`;
    let index = 0;
    const interval = setInterval(() => {
      if (index <= fullText.length) {
        setDisplayText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 25);
    return () => clearInterval(interval);
  }, [cipherType]);

  // Auto-scroll terminal
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [commandHistory, result]);

  // Handle input change
  const handleInputChange = (e) => {
    setSolution(e.target.value.toUpperCase());
  };

  // Handle submit
  const handleSubmit = () => {
    if (!solution.trim()) return;

    // Add to command history
    setCommandHistory(prev => [
      ...prev,
      { type: 'input', text: `> DECRYPT "${solution.trim()}"` }
    ]);

    onSubmit(solution.trim());
    setSolution('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && solution.trim()) {
      handleSubmit();
    }
  };

  // Focus input on terminal click
  const handleTerminalClick = () => {
    inputRef.current?.focus();
  };

  // Update command history when result changes
  useEffect(() => {
    if (result) {
      setCommandHistory(prev => [
        ...prev,
        {
          type: result.correct ? 'success' : 'error',
          text: result.correct
            ? `> ACCESS GRANTED\n> DECRYPTED: ${result.plaintext || 'SUCCESS'}`
            : `> ACCESS DENIED\n> ${result.message || 'INVALID DECRYPTION KEY'}`
        }
      ]);
      if (result.escalation_hint) {
        setCommandHistory(prev => [
          ...prev,
          { type: 'hint', text: `> SYSTEM HINT: ${result.escalation_hint}` }
        ]);
      }
    }
  }, [result]);

  return (
    <div className="space-y-4">
      {/* CRT Monitor Frame */}
      <div className="relative mx-auto max-w-lg">
        {/* Monitor bezel */}
        <div className="bg-gradient-to-b from-zinc-700 via-zinc-800 to-zinc-900 rounded-2xl p-4 shadow-2xl border-2 border-zinc-600">
          {/* Screen with CRT effects */}
          <div
            ref={terminalRef}
            onClick={handleTerminalClick}
            className="relative bg-black rounded-lg h-64 overflow-y-auto cursor-text"
            style={{
              boxShadow: 'inset 0 0 100px rgba(0, 255, 255, 0.1)',
            }}
          >
            {/* Scanline overlay */}
            <div
              className="absolute inset-0 pointer-events-none opacity-10"
              style={{
                background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.3) 0px, transparent 1px, transparent 2px, rgba(0,0,0,0.3) 3px)',
              }}
            />

            {/* Screen curvature overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.3) 100%)',
              }}
            />

            {/* Terminal content */}
            <div className="relative p-4 font-mono text-sm text-cyan-400 space-y-1 z-10">
              {/* Boot sequence text */}
              <pre className="text-pink-400 whitespace-pre-wrap text-xs leading-relaxed">
                {displayText}
              </pre>

              {/* Command history */}
              {commandHistory.map((cmd, idx) => (
                <pre
                  key={idx}
                  className={`whitespace-pre-wrap text-xs leading-relaxed ${
                    cmd.type === 'success' ? 'text-green-400' :
                    cmd.type === 'error' ? 'text-red-400' :
                    cmd.type === 'hint' ? 'text-yellow-400' :
                    'text-cyan-300'
                  }`}
                >
                  {cmd.text}
                </pre>
              ))}

              {/* Current input line */}
              {!solved && (
                <div className="flex items-center text-cyan-400">
                  <span className="text-pink-500">{'>'}</span>
                  <span className="ml-2">{solution}</span>
                  <span
                    className={`ml-0.5 w-2 h-4 bg-cyan-400 ${cursorVisible ? 'opacity-100' : 'opacity-0'}`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Monitor controls */}
          <div className="flex justify-between items-center mt-3 px-4">
            <div className="flex gap-2">
              <div className="w-2 h-2 rounded-full bg-pink-500 shadow-lg shadow-pink-500/50" />
              <div className="w-2 h-2 rounded-full bg-cyan-500 shadow-lg shadow-cyan-500/50" />
            </div>
            <span className="text-xs text-zinc-500 font-mono">VICE-TERM v1.985</span>
            <div className="w-8 h-3 bg-zinc-700 rounded" />
          </div>
        </div>
      </div>

      {/* Ciphertext Display - styled as intercepted transmission */}
      <div className="bg-gradient-to-r from-purple-900/30 via-black to-cyan-900/30 border border-cyan-500/30 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-pink-600/20 to-cyan-600/20 border-b border-cyan-500/30 px-4 py-2 flex items-center gap-2">
          <Zap className="w-4 h-4 text-pink-400" />
          <span className="text-xs text-cyan-300 uppercase font-mono tracking-wider">
            Intercepted Transmission
          </span>
        </div>
        <div className="p-4">
          <p className="font-mono text-lg text-cyan-200 tracking-widest leading-relaxed break-all" style={{ textShadow: '0 0 10px rgba(0, 255, 255, 0.5)' }}>
            {ciphertext}
          </p>
        </div>
      </div>

      {/* Discovered Hints - styled as data fragments */}
      {hints && hints.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-mono text-pink-400">DATA FRAGMENTS RECOVERED</span>
          </div>
          <div className="grid gap-2">
            {hints.map((hint, idx) => (
              <div
                key={hint.id}
                className="bg-gradient-to-r from-pink-900/20 to-purple-900/20 border border-pink-500/30 rounded p-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Terminal className="w-3 h-3 text-pink-400" />
                  <span className="text-xs text-pink-400 font-mono">FRAGMENT_{idx + 1}</span>
                </div>
                <p className="text-sm text-cyan-200 font-mono" style={{ textShadow: '0 0 5px rgba(0, 255, 255, 0.3)' }}>
                  "{hint.hint_phrase}"
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success/Failure Display */}
      {result && (
        <div
          className={`p-4 rounded-lg border transition-all duration-300 ${
            result.correct
              ? 'bg-green-900/20 border-green-500/50 animate-success-glow'
              : 'bg-red-900/20 border-red-500/50 animate-shake'
          }`}
          style={{
            boxShadow: result.correct
              ? '0 0 30px rgba(0, 255, 0, 0.3)'
              : '0 0 20px rgba(255, 0, 0, 0.2)'
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            {result.correct ? (
              <Check className="w-6 h-6 text-green-400 animate-bounce" />
            ) : (
              <X className="w-5 h-5 text-red-400" />
            )}
            <span className={`font-mono font-bold text-lg ${result.correct ? 'text-green-400' : 'text-red-400'}`}>
              {result.correct ? '>>> ACCESS GRANTED <<<' : '>>> ACCESS DENIED <<<'}
            </span>
          </div>
          {result.correct && result.plaintext && (
            <div className="mt-2 p-2 bg-green-900/30 rounded border border-green-500/30">
              <p className="text-green-300 font-mono" style={{ textShadow: '0 0 15px rgba(0, 255, 0, 0.6)' }}>
                DECRYPTED: {result.plaintext}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Input area - hidden input with styled display */}
      <div className="space-y-3">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={solution}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            disabled={loading || solved}
            className="w-full bg-black border-2 border-cyan-500/50 rounded-lg px-4 py-3 text-cyan-300 font-mono tracking-widest placeholder:text-cyan-800 focus:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500/30 disabled:opacity-50"
            placeholder="ENTER DECRYPTION KEY..."
            style={{
              boxShadow: '0 0 10px rgba(0, 255, 255, 0.1)',
              textShadow: '0 0 5px rgba(0, 255, 255, 0.5)',
            }}
          />
        </div>
        <div className="flex justify-between items-center text-xs font-mono">
          <span className="text-pink-400">
            DECRYPTION ATTEMPTS: <span className="text-cyan-300">{attempts || 0}</span>
          </span>
          <span className="text-cyan-500 opacity-50">
            [ENTER TO SUBMIT]
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={handleSubmit}
          disabled={loading || !solution.trim() || solved}
          className="flex-1 font-mono border"
          style={{
            background: 'linear-gradient(135deg, #FF1493 0%, #8B008B 50%, #00CED1 100%)',
            borderColor: 'rgba(255, 20, 147, 0.5)',
            boxShadow: '0 0 20px rgba(255, 20, 147, 0.3)',
          }}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              DECRYPTING...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              EXECUTE DECRYPTION
            </>
          )}
        </Button>
      </div>

      {/* Aesthetic corner decorations */}
      <div className="flex justify-between text-xs font-mono text-cyan-600 opacity-30">
        <span>{'<'}/CIPHER_TERMINAL{'>'}</span>
        <span>SECURE_LINK::ACTIVE</span>
      </div>
    </div>
  );
};

export default TerminalUI;
